import { useIsClient } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as config from "../../../config/config";
import {
  convertGptSummary,
  truncateBenefits,
  truncateSkills,
} from "../../../functions/job";
import { SpanStyled } from "../../advice/AdviceTrendingTopics/styles";
import { AiJobSummarySkeleton } from "../Skeleton/Skeleton";
import {
  AiArrowImage,
  AiSparklesIcon,
  AiSummaryTail,
  AshleyImage,
  BoxContainer,
  HoverIcon,
  JobDetailsCard,
  JobDetailsContainer,
  JobTitleText,
  LinkStyled,
  PlaceholderImage,
  PlaceholderWrapper,
  PromotedContainer,
  PromotedIcon,
  PromotedText,
  ShowMoreText,
  SummaryBulletItem,
  SummaryBulletList,
  SummaryCard,
  SummaryContent,
  SummaryWrapper,
  TextStyled,
} from "./styles";

function AiJobSummary(props) {
  const { onHoverLeave } = props;

  const isClient = useIsClient();
  const { i18n } = useTranslation();
  const isBahasa =
    (i18n?.language || "en").split("-")[0].toLowerCase() === "ms";

  const job = useSelector((state) => state.jobs.hoverJob);
  const fetchingJobs = useSelector((state) => state.jobs.fetchingJobList);

  const [showFullSkills, setShowFullSkills] = useState(false);
  const [showFullBenefits, setShowFullBenefits] = useState(false);

  const spaceCheck = job?.title?.includes(" ");

  // To convert the hexadecimal characters and HTML entities from backend to string format
  const convertedGptSummary = useMemo(
    () => convertGptSummary(job, { preferBahasa: isBahasa }),
    [job, isBahasa]
  );

  const truncatedSkills = useMemo(() => {
    const { fullString, truncated, remainingCount } = truncateSkills(job);

    return showFullSkills || !remainingCount ? (
      fullString
    ) : (
      <>
        {truncated}...{" "}
        <ShowMoreText
          onClick={(e) => {
            e.stopPropagation();
            setShowFullSkills(true);
          }}
        >
          +{remainingCount} more
        </ShowMoreText>
      </>
    );
  }, [job?.skills, showFullSkills]);

  const truncatedBenefits = useMemo(() => {
    const { fullString, truncated, remainingCount } = truncateBenefits(job);

    return showFullBenefits || !remainingCount ? (
      fullString
    ) : (
      <>
        {truncated}...{" "}
        <ShowMoreText
          onClick={(e) => {
            e.stopPropagation();
            setShowFullBenefits(true);
          }}
        >
          +{remainingCount} more
        </ShowMoreText>
      </>
    );
  }, [job?.company?.benefits, showFullBenefits]);

  useEffect(() => {
    setShowFullBenefits(false);
    setShowFullSkills(false);
  }, [job]);

  return (
    <SummaryWrapper onMouseLeave={onHoverLeave}>
      {fetchingJobs ? (
        <AiJobSummarySkeleton />
      ) : Object.keys(job).length > 0 ? (
        <SummaryCard $JobSlotMax={job?.jobSlotType === "job_slot_max"}>
          {job?.jobSlotType === "job_slot_max" && (
            <PromotedContainer>
              <PromotedIcon />
              <PromotedText>Promoted Jobs</PromotedText>
              <PromotedIcon />
            </PromotedContainer>
          )}
          <SummaryContent $JobSlotMax={job?.jobSlotType === "job_slot_max"}>
            <JobTitleText>
              <SpanStyled
                style={{ wordBreak: spaceCheck ? "break-word" : "break-all" }}
              >
                {job?.title}
              </SpanStyled>{" "}
              @{" "}
              <LinkStyled
                href={"/companies/" + job?.company?.slug}
                target="_blank"
              >
                {job?.company?.name}
              </LinkStyled>
            </JobTitleText>

            <BoxContainer flexDirection={"column"}>
              <BoxContainer alignItems={"center"}>
                <BoxContainer alignItems={"flex-end"} gap={"0.25rem"}>
                  <AshleyImage
                    src={
                      config.assetDomain + "/images/hiredly/ashley-image.svg"
                    }
                    alt="ashley-image"
                  />
                  <BoxContainer alignItems={"center"} gap={"0.25rem"}>
                    <AiSparklesIcon />
                    <TextStyled
                      fontSize={"1rem"}
                      fontWeight={"600"}
                      lineHeight={"1.5rem"}
                    >
                      AI-generated summary
                    </TextStyled>
                  </BoxContainer>
                </BoxContainer>
              </BoxContainer>
              <JobDetailsContainer></JobDetailsContainer>
            </BoxContainer>
            {convertedGptSummary.length > 0 && (
              <BoxContainer flexDirection={"column"}>
                <AiSummaryTail />
                <JobDetailsCard>
                  {Array.isArray(convertedGptSummary) ? (
                    <SummaryBulletList>
                      {convertedGptSummary.map((line, index) => (
                        <SummaryBulletItem key={index}>
                          {line}
                        </SummaryBulletItem>
                      ))}
                    </SummaryBulletList>
                  ) : (
                    convertedGptSummary
                  )}
                </JobDetailsCard>
              </BoxContainer>
            )}
            {job?.skills?.length > 0 && (
              <JobDetailsCard type={"skills"}>
                <b>Skills: </b>
                {truncatedSkills}
              </JobDetailsCard>
            )}
            {job?.company?.benefits?.length > 0 && (
              <JobDetailsCard type={"benefits"}>
                <b>Benefits: </b>
                {truncatedBenefits}
              </JobDetailsCard>
            )}
          </SummaryContent>
        </SummaryCard>
      ) : (
        <AiSummaryPlaceholder />
      )}
    </SummaryWrapper>
  );
}

function AiSummaryPlaceholder(props) {
  return (
    <PlaceholderWrapper>
      <PlaceholderImage
        src={config.assetDomain + "/images/hiredly/ai-summary-placeholder.webp"}
      />
      <BoxContainer
        justifyContent={"center"}
        alignItems={"center"}
        gap={"0.5rem"}
        marginTop={"1rem"}
      >
        <HoverIcon />
        <TextStyled
          color={"rgba(0, 0, 0, 0.60)"}
          fontSize={"0.875rem"}
          lineHeight={"1.25rem"}
          letterSpacing={"0.112px"}
        >
          Hover over for a quick job overview.
        </TextStyled>
      </BoxContainer>
      <AiArrowImage
        src={config.assetDomain + "/images/hiredly/ai-arrow.svg"}
        alt="arrow-image"
      />
    </PlaceholderWrapper>
  );
}

export default AiJobSummary;
